import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api from "../../../api/Base";
import { showError } from "../../../utils/showAlert";
import HostApi from '../../../api/host/HostApi';

// Third-party packages.
import * as dayjs from 'dayjs';
import * as relativeTime from 'dayjs/plugin/relativeTime';
import * as customParseFormat from 'dayjs/plugin/customParseFormat';

import TableCSVExport from '../../../component/TableCSVExport';

import { Column, usePagination, useRowSelect, useSortBy, useTable } from 'react-table';

// Context.
import { EventTypeContext } from '../../../context/EventTypeContext';
import { HostContext } from "../../../context/HostContext";

// Types.
import HostsAdminDataTable from '../../../types/host/tables/HostsAdminDataTable';
import HostTarget from "../../../types/host/HostTarget";

// Constants.
import { DATA_LOADING } from '../../../component/Constants';

// Helpers
import { getDefaultPageSize } from "../../../helpers/utils";
import SearchInput from '../../../component/SearchInput';

// Components.
import StatusIcon from '../../../component/StatusIcon';
import HostType from '../../../component/host/HostType';
import ActionButtons from '../../../component/buttons/ActionButtons';
import ReactTable from '../../../component/ReactTable';
import ConditionalRender from "../../../helpers/ConditionalRender";
import IndeterminateCheckbox from '../../../component/IndeterminateCheckbox';
import BulkActionConfirmationModal from '../../../component/BulkActionConfirmationModal';

// DayJS plugins - note, the position of these in this file are important.
dayjs.extend(relativeTime.default);
dayjs.extend(customParseFormat.default);

function List(props: { loading: number, hostsAdminDataTable: HostsAdminDataTable[], isAdmin: boolean }) {
    const { eventTypes } = useContext(EventTypeContext);
    const { hosts, setHosts } = useContext(HostContext);
    const [agents, setAgents] = useState<any[]>([]);
    const [hostsList, setHostsList] = useState<HostsAdminDataTable[]>([]);
    const [bulkActionConfirmation, setBulkActionConfirmation] = useState<{ isStart: boolean, hostNames: string[], ids: number[] | null } | null>(null);
    // True while the bulk start/stop request is in flight — keeps the modal's
    // buttons disabled so it can't be submitted twice.
    const [bulkActionRunning, setBulkActionRunning] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const [tableFilter, setTableFilter] = useState(query.get("filter") || "");
    const initialPageIndex = Number(query.get("page") || 0);
    const initialPageSize = Number(query.get("pageSize") || getDefaultPageSize());


    function clearTableFilter() {

        // Clear any pre-existing filter values.
        setTableFilter('');
    }

    const removeHost = (hostId: number) => {
        const updatedHostsList = hostsList.filter(host => host.id !== hostId);
        setHostsList(updatedHostsList);
    }



    const duplicateHost = async (host: any) => {
        // Generate unique name with "copy" suffix
        const baseName = host.host.name;
        const existingNames = hosts.map(hst => hst.name);
        let newName = `${baseName} copy`;
        let counter = 2;

        while (existingNames.includes(newName)) {
            newName = `${baseName} copy ${counter}`;
            counter++;
        }

        // Create duplicate data based on original host
        const duplicateData = [{
            name: newName,
            host: host.host.host,
            agentid: host.host.agentid,
            type: host.host.type,
            monitor: false, // Start stopped
            debug: host.host.debug,
            port: host.host.port,
            username: host.host.username,
            password: host.host.password,
            passwordchanged: false,
            keyfile: host.host.keyfile,
            region: host.host.region,
            monitoringintervalmilliseconds: host.host.monitoringintervalmilliseconds,
            encrypted: host.host.encrypted
        }];

        try {
            const result = await api.post('host', duplicateData);
            if (result.status === 200) {
                // Refresh hosts list
                const updatedHosts = await HostApi.getHosts();
                setHosts(updatedHosts);
            }
        } catch (error: any) {
            console.error('Failed to duplicate host:', error);
            showError('Failed to duplicate host', error.message || error.toString());
        }
    }

    const data = useMemo(() => hostsList.filter(row => (row.name.toLowerCase().includes(tableFilter.toLowerCase())) || (row.hostAddress.toLowerCase().includes(tableFilter.toLowerCase()))), [hostsList, tableFilter]);

    useEffect(() => {
        setHostsList(props.hostsAdminDataTable)
    }, [props.hostsAdminDataTable]);

    useEffect(() => {

        const getAgents = async() => {
            await api.get(`agent `)
                    .then((response) => {
                        if (response) {
                            setAgents(response.data);
                        }
                    })
                    .catch((error: any) => {
                        console.error('Failed to retrieve agents.', error);
                        showError('Failed to retrieve agents', error.message || error.toString());
                    });
        }
        void getAgents();
    }, []);

    const isHostDeleted = (item: any) => {
        const reason = item?.host?.reason || item.reason
        return reason === 'Deletion in progress';
    }

    const columns: Column[] = useMemo(() => [
        {
            id: 'selection',
            disableSortBy: true,
            headerClassName: 'text-center col-width-2',
            className: 'text-center col-width-2',
            Header: ({ rows, state, toggleRowSelected }: any) => {
                const eligibleRows = rows.filter((row: any) => !isHostDeleted(row.original));
                const selectedCount = eligibleRows.filter((row: any) => state.selectedRowIds[row.id]).length;
                const allSelected = eligibleRows.length > 0 && selectedCount === eligibleRows.length;
                return (
                    <IndeterminateCheckbox
                        checked={allSelected}
                        indeterminate={selectedCount > 0 && !allSelected}
                        onChange={() => eligibleRows.forEach((row: any) => toggleRowSelected(row.id, !allSelected))}
                        aria-label="Select all hosts"
                    />
                );
            },
            Cell: ({ row }: any) => {
                const isDeleted = isHostDeleted(row.original);
                return (
                    <IndeterminateCheckbox
                        {...row.getToggleRowSelectedProps()}
                        checked={!isDeleted && row.isSelected}
                        disabled={isDeleted}
                        aria-label={`Select ${row.values.name}`}
                        title={`Select ${row.values.name}`}
                    />
                );
            }
        },
        {
            accessor: 'id',
            disableSortBy: true
        },
        {
            accessor: 'host',
            disableSortBy: true
        },
        {
            Header: 'Host',
            accessor: 'name',
            className: 'fw-bold',
            defaultCanSort: true,
            sortType: 'string',
            Cell: ({ cell }) => (
                    <React.Fragment>
                        <StatusIcon hostId={cell.row.values.id} removeFromList={removeHost} mode="admin" />
                        {cell.row.values.name}
                    </React.Fragment>
            )
        },
        {
            Header: '',
            accessor: 'type',
            className: 'text-center col-width-5',
            headerClassName: 'text-center col-width-5',
            defaultCanSort: true,
            sortType: 'alphanumeric',
            Cell: ({ cell }) => (
                    <React.Fragment>
                        <HostType host={cell.row.values.host} />
                    </React.Fragment>
            )
        },
        {
            Header: 'Host Address',
            accessor: 'hostAddress',
            className: 'concatenate',
            defaultCanSort: true,
            sortType: 'alphanumeric',
            Cell: ({ cell }) => (
                    cell.row.values.hostAddress
            )
        },
        {
            Header: 'Agent',
            accessor: 'agent',
            sortType: (rowA: any, rowB: any) => {
                return (rowA.values.host.agentid - rowB.values.host.agentid)
            },
            Cell: ({cell}) => {
                const getAgent = agents.find(agent => agent.id === cell.row.values.host.agentid)
                return getAgent ? getAgent.name : ''
            }
        },
        {
            Header: 'Agent Version',
            accessor: 'version',
            sortType: (rowA: any, rowB: any) => {
                return (rowA.values.host.agentid - rowB.values.host.agentid)
            },
            Cell: ({cell}) => {
                const getAgent = agents.find(agent => agent.id === cell.row.values.host.agentid)
                return getAgent?.version ? getAgent.version : ''
            }
        },
        {
            Header: <div className='d-flex justify-content-between'><span className='w-25 text-start'>Last Contacted</span><span>Actions</span></div>,
            accessor: 'actions',
            className: 'actions',
            disableSortBy: true,
            headerClassName: 'actions',
            Cell: ({ cell }) => {
                const isDeleted = isHostDeleted(cell.row.original)
                return isDeleted ? 'Deletion in progress' :  <ActionButtons isAdmin={props.isAdmin} host={cell.row.values.host} eventTypes={eventTypes} onDuplicate={duplicateHost} rowData={cell.row.original}/>
            }
        }
    ], [eventTypes, agents, tableFilter]);

    const tableInstance = useTable({
                columns,
                data,
                getRowId: (row: any) => String(row.id),
                // Data refreshes (start/stop, polling) must not wipe the
                // user's sort, current page, or checkbox selection.
                autoResetSortBy: false,
                autoResetPage: false,
                autoResetSelectedRows: false,
                autoResetFilters: false,
                autoResetExpanded: false,
                autoResetGroupBy: false,
                initialState: { hiddenColumns: ['id', 'host'],
                    pageIndex: initialPageIndex,
                    pageSize: initialPageSize, sortBy: [{ id: 'name', desc: false }] },
            },
            useSortBy, usePagination, useRowSelect);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        toggleAllRowsSelected,
        state: { pageIndex, pageSize, selectedRowIds }
    } = tableInstance;

    // With autoResetPage off, keep the page index valid ourselves: clamp when
    // the data shrinks (e.g. rows filtered out) below the current page.
    useEffect(() => {
        if (pageIndex > 0 && pageIndex >= pageCount) {
            gotoPage(Math.max(0, pageCount - 1));
        }
    }, [pageIndex, pageCount, gotoPage]);

    const selectedIds = useMemo(
        () => data.filter(row => selectedRowIds[String(row.id)] && !isHostDeleted(row)).map(row => row.id),
        [data, selectedRowIds]
    );
    const hasSelection = selectedIds.length > 0;
    const startLabel = hasSelection ? `Start (${selectedIds.length})` : 'Start all';
    const stopLabel = hasSelection ? `Stop (${selectedIds.length})` : 'Stop all';

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        if (tableFilter) {
            params.set('filter', tableFilter);
        } else {
            params.delete('filter');
        }

        params.set('page', pageIndex.toString());
        params.set('pageSize', pageSize.toString());

        navigate({ search: params.toString() }, { replace: true });
    }, [tableFilter, pageIndex, pageSize]);

    useEffect(() => {
        if (tableFilter) {
            gotoPage(0);
        }
    }, [tableFilter]);

    // Transform function to compute agent and version for CSV export
    const transformCsvData = useMemo(() => (csvData: any[]) => {
        return csvData.map((row: any) => {
            // Get original row data (stored in _original by TableCSVExport)
            const originalRow = row._original || row;

            // Compute agent name and version from agents array
            if (originalRow.host?.agentid) {
                const getAgent = agents.find(agent => agent.id === originalRow.host.agentid);
                if (getAgent) {
                    row.agent = getAgent.name || '';
                    row.version = getAgent.version || '';
                } else {
                    // Agent not found, set to empty string
                    row.agent = '';
                    row.version = '';
                }
            } else {
                // No agentid, set to empty string
                row.agent = '';
                row.version = '';
            }

            return row;
        });
    }, [agents]);

    const startStopAll = async (isStart: boolean, ids: number[] | null) => {
        const changedIds: number[] = [];

        const updatedHosts = hosts.map((obj: HostTarget) => {
            const isCandidate = !ids || ids.includes(obj.id);
            if (!isCandidate) {
                return obj;
            }
            const updatedObj = {
                ...obj,
                monitor: isStart,
                password: obj.password === '' ? null : obj.password,
            };

            if (updatedObj.monitor !== obj.monitor && !isHostDeleted(updatedObj)) {
                changedIds.push(updatedObj.id);
                return updatedObj;
            }

            return obj;
        });
        const changedHosts = updatedHosts.filter(host =>
                changedIds.includes(host.id)
        );
        try {
            if (changedIds.length) {
                await api.put('host', changedHosts);
                setHosts(updatedHosts);
                toggleAllRowsSelected(false);
            }
        } catch (error: any) {
            console.error('Failed to update monitor state on backend:', error);
            showError('Failed to update monitor state on backend', error.message || error.toString());
        }
    };

    const openBulkActionConfirmation = (isStart: boolean) => {
        const ids = hasSelection ? selectedIds : null;
        const affectedHosts = hosts.filter((host: HostTarget) => {
            const isCandidate = !ids || ids.includes(host.id);
            return isCandidate && host.monitor !== isStart && !isHostDeleted(host);
        });

        if (affectedHosts.length === 0) {
            return;
        }

        setBulkActionConfirmation({
            isStart,
            hostNames: affectedHosts.map(host => host.name),
            ids
        });
    };

    const confirmBulkAction = async () => {
        if (!bulkActionConfirmation || bulkActionRunning) {
            return;
        }

        setBulkActionRunning(true);
        try {
            await startStopAll(bulkActionConfirmation.isStart, bulkActionConfirmation.ids);
        } finally {
            setBulkActionRunning(false);
            setBulkActionConfirmation(null);
        }
    };

    return (
            <div className="col h-100">
                <div className="card">
                    <div className="card-header">
                        <i className="fal fa-server fa-fw" aria-hidden="true"></i>
                        Monitored hosts
                        <span className="badge bg-info" data-tooltip-id="global-tooltip" data-tooltip-content="Total host count">{data.length.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
                        <i className="collapse-toggle" role="button" data-bs-toggle="collapse" data-bs-target="#collapseHosts" aria-expanded="false" aria-controls="collapseHosts"></i>
                    </div>
                    <div id="collapseHosts" className="card-body collapse show">
                        <div className="row row-cols-1 row-cols-md-2 table-search">
                            <div className="col col-md-10">
                                <ConditionalRender if={props.isAdmin}>
                                    <button type="button" className='btn btn-sm btn-outline-secondary' data-tooltip-id="global-tooltip" data-tooltip-content={startLabel}
                                            onClick={() => openBulkActionConfirmation(true)}>
                                        <i className='fal fa-play fa-fw'></i><span>{startLabel}</span>
                                    </button>
                                    <button type="button" className='btn btn-sm btn-secondary' data-tooltip-id="global-tooltip" data-tooltip-content={stopLabel}
                                            onClick={() => openBulkActionConfirmation(false)}>
                                        <i className='fal fa-stop fa-fw'></i><span>{stopLabel}</span>
                                    </button>
                                </ConditionalRender>
                                <Link to="/admin/hosts/create" role="button" className="btn btn-sm btn-success">
                                    <i className="fal fa-plus-square fa-fw"></i><span>Create</span>
                                </Link>
                                <TableCSVExport
                                        tableInstance={tableInstance}
                                        filename="Hosts"
                                        transformData={transformCsvData}
                                        excludeColumns={['selection']}
                                />
                            </div>
                            <div className="col col-md-2">
                                <SearchInput
                                        value={tableFilter}
                                        onChange={(e) => setTableFilter(e.target.value)}
                                        onClear={clearTableFilter}
                                        autoFocus
                                />
                            </div>
                        </div>
                        <ReactTable
                            getTableProps={getTableProps}
                        getTableBodyProps={getTableBodyProps}
                        headerGroups={headerGroups}
                        prepareRow={prepareRow}
                        page={page}
                        canPreviousPage={canPreviousPage}
                        canNextPage={canNextPage}
                        pageOptions={pageOptions}
                        pageCount={pageCount}
                        gotoPage={gotoPage}
                        nextPage={nextPage}
                        previousPage={previousPage}
                        setPageSize={setPageSize}
                        pageIndex={pageIndex}
                        pageSize={pageSize}
                        dataLength={data.length}
                        error={null}
                        loading={props.loading === DATA_LOADING}
                    />
                    </div>
                </div>

                {bulkActionConfirmation && (
                    <BulkActionConfirmationModal
                        isStart={bulkActionConfirmation.isStart}
                        names={bulkActionConfirmation.hostNames}
                        noun="host"
                        overflowNoun="host"
                        isSelection={bulkActionConfirmation.ids !== null}
                        running={bulkActionRunning}
                        onCancel={() => setBulkActionConfirmation(null)}
                        onConfirm={confirmBulkAction}
                    />
                )}
            </div>
    )
}

export default List;
